import { Icon, type IconName } from './Icon';

export interface LifecycleStep {
  key: string;
  label: string;
  icon: IconName;
  sub?: string;
}

/**
 * Stepper di ciclo di vita riusabile (estratto da Quote/Invoice).
 * - `currentIdx`: indice dello step attivo (precedenti = completati).
 * - `onStepClick`: se passato, gli step diventano CLICCABILI (es. cambiare lo
 *   stato del corso o di un ingaggio body-rental dalla pagina).
 * - `activeTone`: 'alert' rende lo step attivo rosso (es. fattura SCADUTA).
 */
export function LifecycleStepper({
  steps,
  currentIdx,
  onStepClick,
  maxWidth = 820,
  activeTone,
}: {
  steps: LifecycleStep[];
  currentIdx: number;
  onStepClick?: (idx: number, step: LifecycleStep) => void;
  maxWidth?: number;
  activeTone?: 'alert';
}) {
  const clickable = !!onStepClick;
  return (
    <div className="card mb-5">
      <div className="card-bd py-5">
        <div className="flex items-stretch justify-between gap-1 mx-auto lifecycle-stepper" style={{ maxWidth }}>
          {steps.map((s, i) => {
            const completed = i < currentIdx;
            const active = i === currentIdx;
            return (
              <div key={s.key} className="flex items-center flex-1 last:flex-none lifecycle-step">
                <div className="flex flex-col items-center lifecycle-node" style={{ minWidth: 100 }}>
                  <button
                    type="button"
                    disabled={!clickable}
                    onClick={() => onStepClick?.(i, s)}
                    title={clickable ? `Imposta: ${s.label}` : undefined}
                    className="w-10 h-10 rounded-full grid place-items-center transition-all"
                    style={{
                      background: completed ? 'var(--ok)' : active ? (activeTone === 'alert' ? 'var(--alert)' : 'var(--ink)') : 'var(--paper-2)',
                      color: completed || active ? '#fff' : 'var(--mute)',
                      boxShadow: active ? '0 0 0 4px color-mix(in oklab, var(--ink) 6%, transparent)' : 'none',
                      border: 'none',
                      cursor: clickable ? 'pointer' : 'default',
                    }}
                  >
                    {completed ? <Icon name="check" size={14} /> : <Icon name={s.icon} size={14} />}
                  </button>
                  <div className="mt-2 text-center">
                    <div className={`text-[13px] font-semibold ${active || completed ? 'text-ink' : 'mute-2'}`}>{s.label}</div>
                    {s.sub && <div className="text-[12px] mute mt-0.5">{s.sub}</div>}
                  </div>
                </div>
                {i < steps.length - 1 && (
                  <div className="flex-1 h-[2px] mt-5 mx-1 rounded-full overflow-hidden" style={{ background: 'var(--line)' }}>
                    <div
                      className="h-full rounded-full"
                      style={{ width: completed ? '100%' : '0%', background: 'var(--ok)', transition: 'width var(--dur-slower) var(--ease-out)' }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export interface LifecycleAction {
  title: string;
  body: string;
  cta: string;
  icon: IconName;
  tone: 'primary' | 'ok' | 'warn' | 'info';
  onClick: () => void;
  loading?: boolean;
}

export function NextActionCard({ action }: { action: LifecycleAction }) {
  const bg = action.tone === 'primary' ? 'var(--ink)'
    : action.tone === 'ok' ? 'var(--ok)'
    : action.tone === 'warn' ? 'var(--warn)'
    : 'var(--info)';
  return (
    <div className="p-5 flex items-center gap-4 next-action-card" style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-card)' }}>
      <div className="w-12 h-12 rounded-full grid place-items-center shrink-0" style={{ background: bg, color: '#fff' }}>
        <Icon name={action.icon} size={18} />
      </div>
      <div className="flex-1">
        <div className="text-[16px] font-semibold mb-1">{action.title}</div>
        <div className="mute text-[13px] leading-[1.5]">{action.body}</div>
      </div>
      <button className={`btn next-action-cta ${action.tone === 'primary' ? 'btn-primary' : ''}`} onClick={action.onClick} disabled={action.loading}>
        <Icon name={action.icon} size={12} /> {action.loading ? 'Attendi…' : action.cta}
      </button>
    </div>
  );
}
